import React, { FC, useContext, useState } from 'react';
import { Button, Box, Heading, Flex, Text } from '@resideo/blueprint-react';
import ContactForm from 'components/cart/shipping/ContactForm';
import ShippingAddressForm from 'components/cart/shipping/BillingOrShippingAddressForm';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CartRoutesUpdateMutationResponse } from './__generated__/CartRoutesUpdateMutation.graphql';
import { useHistory } from 'react-router-dom';
import CheckoutSteps from 'components/cart/CheckoutSteps';
import { cartLinksData } from '../../../config/cartLinks';
import { CartContext } from 'routes/ServicesAndSubscriptions/CartProvider';
import OrderSummaryDesktop from 'components/cart/OrderSummaryDesktop';
import ShippingMethod from 'components/cart/shipping/ShippingMethod';
import GoBackButton from 'components/common/GoBackButton';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useToast } from 'components/common/Toast';
import Alert from 'components/common/Alert';
import OrderSummaryMobile from '../../../components/cart/OrderSummaryMobile';

export const PageTitle = styled(Heading)`
  text-transform: uppercase;
  padding-bottom: 24px;
  color: ${({ theme }) => theme.colors.textLight3};
  font-family: 'URW_Din_CondBold';
  font-size: 2.813rem;
  letter-spacing: 1.47px;
  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const Shipping: FC<any> = ({
  // partnerAccountId,
  digitalRiverBillingId,
  updateCheckoutMutation,
  defaultShipTo,
  defaultBillTo,
  viewerData,
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const history = useHistory();
  const [errors, setErrors] = useState({}) as any;

  const { state, cartActions } = useContext(CartContext);

  const order =
    localStorage.getItem('checkoutDetails') &&
    localStorage.getItem('checkoutDetails') !== 'undefined'
      ? JSON.parse(localStorage.getItem('checkoutDetails') || '')
      : {};

  const handleSubmit = values => {
    cartActions.setIsFetching(true);
    const {
      addressLine1,
      addressLine2,
      city,
      stateProvinceRegionCode,
      zipPostalCode,
      countryCode,
      contactName,
      companyName,
    } = values.shippingAddress;

    const keyId = 'id';
    delete values.shippingAddress[keyId];

    updateCheckoutMutation({
      variables: {
        digitalRiverBillingId,
        checkoutId: order.id,
        input: {
          shipTo: {
            firstName: defaultShipTo?.firstName,
            lastName: defaultShipTo?.lastName,
            email: defaultShipTo?.email,
            organization: companyName,
            address: {
              addressLine1,
              addressLine2,
              city,
              stateProvinceRegionCode,
              zipPostalCode,
              countryCode,
              contactName,
            },
          },
          billTo: {
            firstName: defaultBillTo?.firstName,
            lastName: defaultBillTo?.lastName,
            email: defaultBillTo?.email,
            address: defaultBillTo?.address,
          },
          shippingChoice: {
            description:
              values.shippingMethod === 'standard'
                ? t('cart.shipping.standardGroundBusinessDays')
                : values.shippingMethod === 'expressSecondDay'
                ? t('cart.shipping.domesticSecondDayAir')
                : values.shippingMethod === 'expressNextDay'
                ? t('cart.shipping.domesticNextDayAir')
                : t('cart.shipping.standardGroundBusinessDays'),
            serviceLevel:
              values.shippingMethod === 'standard'
                ? 'STANDARD_GROUND'
                : values.shippingMethod === 'expressSecondDay'
                ? 'EXPRESS_2_DAY'
                : values.shippingMethod === 'expressNextDay'
                ? 'EXPRESS_NEXT_DAY'
                : 'STANDARD_GROUND',
          },
        },
      },
      onError: err => {
        cartActions.setIsFetching(false);
        console.log('error', err);
        toast(
          <Alert variant='error'>
            <Text>{err.message}</Text>
          </Alert>,
          { duration: 10000 }
        );
      },
      onCompleted: (response: CartRoutesUpdateMutationResponse) => {
        cartActions.setIsFetching(false);
        if (
          response &&
          response.digitalRiverCheckoutUpdate &&
          response.digitalRiverCheckoutUpdate.errors &&
          response.digitalRiverCheckoutUpdate.errors.length > 0
        ) {
          console.log('error', response.digitalRiverCheckoutUpdate.errors);
          toast(
            <Alert variant='error'>
              <Text>
                {JSON.stringify(
                  response.digitalRiverCheckoutUpdate.errors[0]?.message
                )}
              </Text>
            </Alert>,
            { duration: 10000 }
          );
          return null;
        }
        const updatedCheckout = {
          ...response?.digitalRiverCheckoutUpdate?.digitalRiverCheckout,
          lineItems: response?.digitalRiverCheckoutUpdate?.digitalRiverCheckout?.lineItems?.map(
            ele => ({
              ...ele,
              ...state.cartItems.find(item => item.sku === ele?.sku),
            })
          ),
        };

        cartActions.setCheckoutDetails(updatedCheckout);
        // const orderPlusServiceLevel = {
        //   ...updatedCheckout,
        //   serviceLevel:
        //     values.shippingMethod === 'standard'
        //       ? 'STANDARD_GROUND'
        //       : 'EXPRESS_2_DAY',
        // };
        localStorage.setItem(
          'checkoutDetails',
          JSON.stringify(updatedCheckout)
        );
        history.push('/cart/payment');
      },
    });
    // }
  };

  const data = () => ({
    email: '',
    phone: '',
    willReceiveUpdates: false,
    additionalEmails: '',
    shippingMethod: '',
    promoCode: '',
    multipleAddresses: [],
    billingAddress: {
      companyName: '',
      contactName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      stateProvinceRegionCode: 'AL',
      countryCode: 'US',
      zipPostalCode: '',
    },
    shippingAddress: {
      companyName: '',
      contactName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      stateProvinceRegionCode: '',
      countryCode: '',
      zipPostalCode: '',
    },
    shippingAddressEdit: {
      companyName: '',
      contactName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      stateProvinceRegionCode: '',
      countryCode: '',
      zipPostalCode: '',
    },
  });

  const ShippingSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email')
      .required('Email is required'),
    phone: Yup.string().required('Phone number is required'),
    willReceiveUpdates: Yup.boolean(),
    additionalEmails: Yup.string(),
    shippingMethod: Yup.string(),
    promoCode: Yup.string(),
    shippingAddress: Yup.object().shape({
      companyName: Yup.string(),
      contactName: Yup.string().required('Required'),
      addressLine1: Yup.string().required('Required'),
      addressLine2: Yup.string(),
      city: Yup.string().required('Required'),
      stateProvinceRegionCode: Yup.string(),
      countryCode: Yup.string(),
      zipPostalCode: Yup.string().required('Required'),
    }),
  });

  return (
    <Box data-test-shipping-form>
      <CheckoutSteps step2 />
      <Flex marginBottom='medium' marginX='large' flexDirection='column'>
        <PageTitle
          data-test-page-title
          paddingTop={['medium', 'medium', 'large', 'large']}>
          {t('cart.shipping.checkout')}
        </PageTitle>
        <Formik
          onSubmit={handleSubmit}
          initialValues={data()}
          validationSchema={ShippingSchema}>
          {({ values, setFieldValue, errors: fErrors }) => (
            <Form>
              <Flex paddingBottom={['medium', 'medium', 'medium', '0']}>
                <OrderSummaryMobile values={values} />
              </Flex>
              <Flex flexDirection={['column', 'column', 'column', 'row']}>
                <Box
                  width={['100%', '100%', '100%', '49%']}
                  marginRight='medium'>
                  <ContactForm
                    values={values}
                    setFieldValue={setFieldValue}
                    viewerData={viewerData}
                    fErrors={fErrors}
                  />
                  <ShippingAddressForm
                    values={values}
                    setFieldValue={setFieldValue}
                    setErrors={setErrors}
                    errors={errors}
                  />
                  <ShippingMethod setFieldValue={setFieldValue} />
                </Box>
                <Box width={['100%', '100%', '100%', '49%']}>
                  <OrderSummaryDesktop
                    setFieldValue={setFieldValue}
                    values={values}
                  />
                  <Flex
                    justifyContent='flex-end'
                    alignItems='center'
                    paddingTop='large'
                    flexDirection={[
                      'column-reverse',
                      'column-reverse',
                      'column-reverse',
                      'row',
                    ]}>
                    <GoBackButton
                      url={`${process.env.PUBLIC_URL ?? ''}${cartLinksData[0]}`}
                      text={t('cart.backToCart')}
                    />
                    <Button
                      data-test-continue-to-payment-btn
                      disabled={
                        state.isFetching ||
                        !values.email ||
                        !values.shippingAddress.contactName
                      }
                      type='submit'
                      marginLeft='medium'
                      paddingX={['auto', 'auto', 'large', 'xLarge']}
                      width={['100%', '100%', 'auto', 'auto']}
                      marginBottom={['large', 'large', 0, 0]}
                      variant='primary'>
                      {t('cart.payment.continueToPayment')}
                    </Button>
                  </Flex>
                </Box>
              </Flex>
            </Form>
          )}
        </Formik>
      </Flex>
    </Box>
  );
};

export default Shipping;
